import {Client} from 'thruway.js';
import {Observable} from "rxjs";

// thruway.js turns a WAMP ABORT (msg code 3) into a plain Error before it ever
// reaches onClose, so an auth failure during login / two-factor auth surfaces
// only via onError. The Error message it builds is:
//   'Connection ended because ' + JSON.stringify(details) + reason
// (see thruway.js Client.js). Recover the structured {details, reason} from it
// so we can route the failure through the normal $wamp.close path; returns null
// for any other error (transport timeout, network drop, etc.).
function parseWampAbort(err) {
  const msg = err && err.message;
  const marker = 'Connection ended because ';
  if (typeof msg !== 'string' || msg.indexOf(marker) !== 0) {
    return null;
  }
  const rest = msg.slice(marker.length);
  // details is JSON ({...}) immediately followed by the reason URI, which never
  // contains a '}', so the last '}' is the end of the details object.
  const end = rest.lastIndexOf('}');
  if (end === -1) {
    return null;
  }
  try {
    const details = JSON.parse(rest.slice(0, end + 1));
    const reason = rest.slice(end + 1);
    details.reason = reason;
    return {reason: reason, details: details};
  } catch (e) {
    return null;
  }
}

export default function $thruway(settings, $rootScope, $q) {

  const prefix = '$wamp';

  const config = Observable.of({
    url: settings.wamp_url,
    realm: settings.realm,
    autoOpen: false,

    options: {'authmethods': [settings.auth_method]}
  });

  const c2 = Observable.of({
    url: 'ws://127.0.0.1:9090',
    realm: settings.realm,
  }).delay(5000);

  // thruway.js reconnects by itself after any transport error (nginx or the
  // router down, a dropped socket) with a growing delay. Its default tops out
  // at 60 s, which is a long time to sit dark once the router is back; cap it.
  const wamp = new Client(config, undefined, {retryOptions: {maxRetryDelay: 15000}});

  wamp.onChallenge(challenge => {
    return challenge.flatMap(msg => {
      const onChallengeDeferred = $q.defer();

      $rootScope.$broadcast(prefix + ".onchallenge", {
        promise: onChallengeDeferred,
        method: msg.authMethod,
        extra: msg.extra
      });

      return onChallengeDeferred.promise;
    });

  });

  wamp.onOpen.subscribe(({welcomeMsg}) => {
    $rootScope.$broadcast(prefix + ".open", {
      session: welcomeMsg.sessionId,
      details: welcomeMsg.details
    });
  });

  wamp.onClose.subscribe((msg) => {
    let details = msg.details;
    details.reason = msg.uri || msg.reason;
    return $rootScope.$broadcast(prefix + ".close", {
      reason: msg.uri || msg.reason,
      details: details
    })
  });

  wamp.onError.subscribe((err) => {

    // An auth failure (incl. two-factor) comes through here as an ABORT-derived
    // error, not as onClose. Recover its message/reason and re-broadcast it on
    // the $wamp.close channel the login flow already listens on, so the user
    // actually sees why login failed instead of nothing.
    const abort = parseWampAbort(err);
    if (!abort) {
      // Not a WAMP ABORT: the socket could not be opened or was dropped
      // (nginx down, router down, network). thruway.js is already retrying;
      // tell the UI so it can say so instead of sitting silent.
      $rootScope.$broadcast(prefix + ".disconnected", {message: err && err.message});
      return;
    }

    if (abort.reason && abort.reason.endsWith('eci.login.failure')) {
      window.localStorage.clear();
    }

    $rootScope.$broadcast(prefix + ".close", {
      reason: abort.reason,
      details: abort.details
    });
  });

  let open = false;
  return {
    thruway: wamp,
    open: function () {
      if (!open) {
        wamp.open();
        open = true;
      }
    },
    setAuthId: function (authid, open) {
      console.warn('setAuthId not implemented');
    },
    close: function () {
      wamp.close();
    },
    subscribe: function (topic, handler, options, subscribedCallback) {
      return wamp.topic(topic, options)
          .map(event => {
            //This is to keep with the behavior of autobhanjs
            if (event.args && event.args.length === 1) {
              return event.args;
            }
            return event;
          })
          .subscribe(handler);
    },
    subscribeOnScope: function (scope, channel, callback) {
      console.warn('subscribeOnScope not implemented');
    },
    unsubscribe: function (subscription) {
      subscription.dispose();
    },
    publish: function (topic, args, kwargs, options) {
      console.warn('publish not implemented');
    },
    register: function (procedure, endpoint, options) {
      console.warn('register not implemented');
    },
    unregister: function (registration) {
      console.warn('unregister not implemented');
    },
    call: function (procedure, args, kwargs, options) {
      return wamp.call(procedure, args, kwargs, options)
          .map(res => {
            //This is to keep with the behavior of autobhanjs
            if (res.args && res.args.length === 1) {
              return res.args[0];
            }
            return res;
          })
          .toPromise();
    }
  };
}
